/**
 * TD-049 — Pending Reviews inbox.
 *
 * Coop-admin (or admin) lands here from the sidebar and sees every
 * `farmer_profile_edit_requests` row with status='pending' targeting a
 * farmer in a cooperative they admin. Each card renders a diff of
 * proposed vs current values, plus [Approve] / [Reject] buttons that
 * PATCH `/farmer_profile_edit_requests/:id` with an optional reviewer
 * note.
 *
 * On success, the row disappears from the list and the farmers query
 * is invalidated so `pendingEditRequestCount` redraws on Farmers.
 */
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Skeleton,
  Stack,
  Chip,
  Text,
  TextField,
} from '@fse/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  getEditRequestsControllerListQueryKey,
  useEditRequestsControllerList,
  useEditRequestsControllerReview,
} from '../api/generated/farmer-profile-edit-requests/farmer-profile-edit-requests';
import type {
  EditRequestListItemDto,
  EditRequestListItemDtoCurrentPreferredLang as PreferredLang,
} from '../api/generated/models';

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

// Mirrors the PREF_LANGS map in _app.farmers.new.tsx — keep in sync. Maps the
// language enum to an i18n key resolved via t() at render (pendingReviews.lang.*).
const LANG_LABEL_KEY: Record<PreferredLang, string> = {
  en: 'pendingReviews.lang.en',
  ts: 'pendingReviews.lang.ts',
  nso: 'pendingReviews.lang.nso',
  xi: 'pendingReviews.lang.xi',
};

function fmtGps(gps: { lat: number; lng: number } | null): string {
  if (!gps) return '—';
  return `${gps.lat.toFixed(4)}, ${gps.lng.toFixed(4)}`;
}

export function PendingReviewsScreen() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const listQuery = useEditRequestsControllerList({ status: 'pending', limit: 50, offset: 0 });

  function refresh() {
    queryClient.invalidateQueries({
      queryKey: getEditRequestsControllerListQueryKey({ status: 'pending', limit: 50, offset: 0 }),
    });
    // Farmers list shows pendingEditRequestCount — refresh that too so the
    // badge drops in lockstep with the action here.
    queryClient.invalidateQueries({ queryKey: ['farmers'] });
  }

  return (
    <Stack spacing={3}>
      <Box>
        <Text variant="headline-medium" as="h2">
          {t('pendingReviews.title')}
        </Text>
        <Box sx={{ mt: 0.5 }}>
          <Text variant="body-medium" color="text.secondary">
            {t('pendingReviews.subtitle')}
          </Text>
        </Box>
      </Box>

      {listQuery.isPending ? (
        <Stack spacing={2}>
          <Skeleton variant="rectangular" height={120} />
          <Skeleton variant="rectangular" height={120} />
        </Stack>
      ) : listQuery.isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => listQuery.refetch()}>
              {t('pendingReviews.retry')}
            </Button>
          }
        >
          {t('pendingReviews.loadError', { error: String(listQuery.error) })}
        </Alert>
      ) : listQuery.data.data.length === 0 ? (
        <Card>
          <CardHeader title={t('pendingReviews.empty.title')} />
          <CardContent>
            <Text variant="body-medium" color="text.secondary">
              {t('pendingReviews.empty.body')}
            </Text>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {listQuery.data.data.map((req) => (
            <RequestCard key={req.id} request={req} onReviewed={refresh} />
          ))}
        </Stack>
      )}
    </Stack>
  );
}

interface RequestCardProps {
  request: EditRequestListItemDto;
  onReviewed: () => void;
}

function RequestCard({ request, onReviewed }: RequestCardProps) {
  const { t } = useTranslation();
  const [reviewerNote, setReviewerNote] = useState('');
  const reviewMutation = useEditRequestsControllerReview({
    mutation: {
      onSuccess: () => {
        onReviewed();
      },
    },
  });

  const submit = (action: 'approved' | 'rejected') => {
    reviewMutation.mutate({
      id: request.id,
      data: {
        action,
        reviewerNote: reviewerNote.trim() ? reviewerNote.trim() : undefined,
      },
    });
  };

  const diffRows: Array<{ field: string; current: string; proposed: string }> = [];
  if (request.proposedDisplayName !== null) {
    diffRows.push({
      field: t('pendingReviews.field.name'),
      current: request.farmerDisplayName,
      proposed: request.proposedDisplayName,
    });
  }
  if (request.proposedPreferredLang !== null) {
    diffRows.push({
      field: t('pendingReviews.field.language'),
      current: t(LANG_LABEL_KEY[request.currentPreferredLang]),
      proposed: t(LANG_LABEL_KEY[request.proposedPreferredLang]),
    });
  }
  if (request.proposedTreeCount !== null) {
    diffRows.push({
      field: t('pendingReviews.field.treeCount'),
      current: String(request.currentTreeCount),
      proposed: String(request.proposedTreeCount),
    });
  }
  if (request.proposedEstCapacityKg !== null) {
    diffRows.push({
      field: t('pendingReviews.field.estCapacity'),
      current: request.currentEstCapacityKg == null ? '—' : String(request.currentEstCapacityKg),
      proposed: String(request.proposedEstCapacityKg),
    });
  }
  if (request.proposedFarmGps !== null) {
    diffRows.push({
      field: t('pendingReviews.field.gps'),
      current: fmtGps(request.currentFarmGps),
      proposed: fmtGps(request.proposedFarmGps),
    });
  }

  const submittedByOther = request.requestedByUserId !== request.userId;

  return (
    <Card variant="outlined">
      <CardHeader
        title={
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Text variant="title-medium" as="span">
              {request.farmerDisplayName}
            </Text>
            <Chip kind="input" color="neutral" label={request.farmerPhone} />
            <Chip kind="assist" color="success" label={request.cooperativeName} />
          </Stack>
        }
        subheader={
          <Text variant="label-medium" color="text.secondary">
            {submittedByOther
              ? t('pendingReviews.card.submittedByAdmin', {
                  date: dateFormatter.format(new Date(request.createdAt)),
                })
              : t('pendingReviews.card.submittedByFarmer', {
                  date: dateFormatter.format(new Date(request.createdAt)),
                })}
          </Text>
        }
      />
      <CardContent>
        <Stack spacing={2}>
          {diffRows.length === 0 ? (
            <Alert severity="info">{t('pendingReviews.card.noChanges')}</Alert>
          ) : (
            <Stack spacing={0.5}>
              {diffRows.map((row) => (
                <DiffRow key={row.field} {...row} />
              ))}
            </Stack>
          )}

          {request.note ? (
            <Box>
              <Text variant="label-medium" color="text.secondary">
                {t('pendingReviews.card.noteFromRequester')}
              </Text>
              <Box sx={{ whiteSpace: 'pre-wrap', mt: 0.25 }}>
                <Text variant="body-medium">{request.note}</Text>
              </Box>
            </Box>
          ) : null}

          <TextField
            label={t('pendingReviews.card.reviewerNoteLabel')}
            placeholder={t('pendingReviews.card.reviewerNotePlaceholder')}
            value={reviewerNote}
            onChange={(e) => setReviewerNote(e.target.value)}
            multiline
            minRows={1}
            maxRows={4}
            disabled={reviewMutation.isPending}
          />

          {reviewMutation.isError ? (
            <Alert severity="error">
              {t('pendingReviews.card.decisionError', { error: String(reviewMutation.error) })}
            </Alert>
          ) : null}

          <Stack direction="row" spacing={1.5} sx={{ justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              color="error"
              disabled={reviewMutation.isPending}
              onClick={() => submit('rejected')}
            >
              {t('pendingReviews.card.reject')}
            </Button>
            <Button
              variant="contained"
              disabled={reviewMutation.isPending}
              onClick={() => submit('approved')}
            >
              {reviewMutation.isPending
                ? t('pendingReviews.card.saving')
                : t('pendingReviews.card.approve')}
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

interface DiffRowProps {
  field: string;
  current: string;
  proposed: string;
}

function DiffRow({ field, current, proposed }: DiffRowProps) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '180px 1fr auto 1fr',
        alignItems: 'baseline',
        gap: 1,
        py: 0.5,
      }}
    >
      <Text variant="body-medium" color="text.secondary">
        {field}
      </Text>
      <Box sx={{ textDecoration: 'line-through' }}>
        <Text variant="body-medium" color="text.secondary">
          {current}
        </Text>
      </Box>
      <Text variant="body-medium" color="text.secondary">
        →
      </Text>
      <Text variant="title-medium" as="span">
        {proposed}
      </Text>
    </Box>
  );
}
